import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency, Icons } from '@cleanpro/common-ui';
import { useGrowthOps } from '../hooks/useGrowthOps';
import type { GrowthProvider } from '../types';

export default function ProviderCampaignTable() {
  const { providers, loading, toggleGrowthAds, togglePause } = useGrowthOps();
  const [searchTerm, setSearchTerm] = useState('');
  const [actioningId, setActioningId] = useState<string | null>(null);

  const filtered = providers.filter(
    (p) =>
      p.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleAds = async (provider: GrowthProvider) => {
    setActioningId(provider.id);
    try {
      await toggleGrowthAds(provider.id, !provider.growthAdsEnabled);
    } catch {
      // Error handled in context
    } finally {
      setActioningId(null);
    }
  };

  const handleTogglePause = async (provider: GrowthProvider) => {
    setActioningId(provider.id);
    try {
      await togglePause(provider.id, !provider.growthAdsPaused);
    } catch {
      // Error handled in context
    } finally {
      setActioningId(null);
    }
  };

  if (loading) {
    return <div className="h-64 animate-pulse rounded-lg bg-gray-100" />;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Growth Providers ({providers.length})
          </h3>
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-md border border-gray-300 py-1.5 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Provider
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Plan
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Wallet
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                Campaigns
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                Leads/Week
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Weekly Spend
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                  {searchTerm ? 'No providers match your search.' : 'No Growth providers found.'}
                </td>
              </tr>
            ) : (
              filtered.map((provider) => (
                <ProviderRow
                  key={provider.id}
                  provider={provider}
                  isActioning={actioningId === provider.id}
                  onToggleAds={() => handleToggleAds(provider)}
                  onTogglePause={() => handleTogglePause(provider)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProviderRow({
  provider,
  isActioning,
  onToggleAds,
  onTogglePause,
}: {
  provider: GrowthProvider;
  isActioning: boolean;
  onToggleAds: () => void;
  onTogglePause: () => void;
}) {
  const statusLabel = !provider.growthAdsEnabled
    ? 'Disabled'
    : provider.growthAdsPaused
      ? 'Paused'
      : 'Active';

  const statusColor = !provider.growthAdsEnabled
    ? 'bg-gray-100 text-gray-700'
    : provider.growthAdsPaused
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-green-100 text-green-800';

  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-4 py-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{provider.businessName}</p>
          <p className="text-xs text-gray-500">{provider.email}</p>
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm capitalize text-gray-600">
        {provider.subscriptionPlan}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
        <span className={provider.pauseThreshold > 0 && provider.walletBalance < provider.pauseThreshold ? 'font-medium text-red-600' : 'text-gray-900'}>
          {formatCurrency(provider.walletBalance)}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-center">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>
          {statusLabel}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-center text-sm text-gray-600">
        {provider.activeCampaigns}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-center text-sm text-gray-600">
        {provider.leadsThisWeek} / {provider.maxLeadsPerWeek}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-600">
        {formatCurrency(provider.weeklySpend)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={onToggleAds}
            disabled={isActioning}
            title={provider.growthAdsEnabled ? 'Disable ads' : 'Enable ads'}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            {provider.growthAdsEnabled ? (
              <Icons.XCircle className="h-4 w-4" />
            ) : (
              <Icons.CheckCircle className="h-4 w-4" />
            )}
          </button>
          {provider.growthAdsEnabled && (
            <button
              onClick={onTogglePause}
              disabled={isActioning}
              title={provider.growthAdsPaused ? 'Resume' : 'Pause'}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            >
              {provider.growthAdsPaused ? (
                <Icons.Play className="h-4 w-4" />
              ) : (
                <Icons.Pause className="h-4 w-4" />
              )}
            </button>
          )}
          <Link
            to={`/growth-ops/per-network/${provider.id}`}
            title="View per-network report"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
          >
            <Icons.BarChart3 className="h-4 w-4" />
          </Link>
        </div>
      </td>
    </tr>
  );
}
